import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldCheck, Mail, Lock, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { OctaPosMark } from '@/components/OctaPosMark'
import { ThemeToggle } from '@/components/ThemeToggle'
import { usePlatformSession } from '@/stores/platformSession'
import { ApiError } from '@/lib/api'

/**
 * Sign-in for the people who create tenants, not the people who run a till.
 *
 * Deliberately a separate page from the tenant login rather than another field on it: the two
 * kinds of account live in different databases and a platform credential must never be typeable
 * into a form that also accepts a store code, or the two could be confused with each other.
 */
export function PlatformLoginPage() {
  const navigate = useNavigate()
  const login = usePlatformSession((state) => state.login)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      await login(email, password)
      navigate('/platform', { replace: true })
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? (caught.detail ?? caught.message)
          : 'Could not reach the server. Check the connection and try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="safe-bottom flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="fixed right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <OctaPosMark title={null} className="h-14 w-14 text-[var(--accent)]" />
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">OctaPOS Platform</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-[var(--text-secondary)]">
            <ShieldCheck className="h-4 w-4" />
            Operator sign-in, not a store login
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="surface-raised flex flex-col gap-4 rounded-[var(--radius-card)] border p-6 shadow-sm"
        >
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@octapos.com"
            leading={<Mail className="h-4 w-4" />}
            autoComplete="username"
            required
            autoFocus
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            leading={<Lock className="h-4 w-4" />}
            autoComplete="current-password"
            required
          />

          {error && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" full disabled={isSubmitting}>
            {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-[var(--text-secondary)]">
          Stays signed in on this device until you sign out.
        </p>
      </div>
    </div>
  )
}
